import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CSS/ConfiguracionCookies.css';

const ConfiguracionCookies = () => {
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always enabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Load saved preferences from localStorage
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleToggle = (cookieType) => {
    if (cookieType === 'necessary') return; // Can't disable necessary cookies

    setCookiePreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    localStorage.setItem('cookieConsent', 'true');
    alert('Tus preferencias de cookies han sido guardadas correctamente.');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsent', 'true');
    alert('Has aceptado todas las cookies.');
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookieConsent', 'true');
    alert('Has rechazado todas las cookies opcionales. Solo se utilizarán las cookies necesarias.');
  };

  return (
    <div className="legal-page-container">
      <div className="legal-page-content">
        <h1>Configuración de Cookies</h1>

        <section className="legal-section">
          <p>
            En esta página puedes gestionar tus preferencias sobre las cookies que utilizamos en nuestro sitio web.
            Puedes aceptar o rechazar el uso de cada tipo de cookie, excepto las cookies necesarias que son
            imprescindibles para el funcionamiento del sitio web.
          </p>
          <p>
            Para obtener más información sobre qué son las cookies y cómo las utilizamos, consulta nuestra{' '}
            <Link to="/politica-cookies" className="legal-inline-link">Política de Cookies</Link>.
          </p>
        </section>

        <div className="cookie-settings">
          <div className="cookie-setting-item">
            <div className="cookie-setting-header">
              <div className="cookie-setting-info">
                <h3>Cookies Necesarias</h3>
                <span className="cookie-badge cookie-badge-required">Siempre activas</span>
              </div>
              <div className="cookie-toggle">
                <input
                  type="checkbox"
                  id="necessary"
                  checked={cookiePreferences.necessary}
                  disabled
                  className="cookie-checkbox"
                />
                <label htmlFor="necessary" className="cookie-switch disabled"></label>
              </div>
            </div>
            <p className="cookie-setting-description">
              Estas cookies son esenciales para el funcionamiento del sitio web y no pueden ser desactivadas.
              Se utilizan para funciones básicas como la navegación por la página y el acceso a áreas seguras.
              Sin estas cookies, el sitio web no puede funcionar correctamente.
            </p>
          </div>

          <div className="cookie-setting-item">
            <div className="cookie-setting-header">
              <div className="cookie-setting-info">
                <h3>Cookies de Preferencias</h3>
                <span className={`cookie-badge ${cookiePreferences.preferences ? 'cookie-badge-active' : 'cookie-badge-inactive'}`}>
                  {cookiePreferences.preferences ? 'Activas' : 'Inactivas'}
                </span>
              </div>
              <div className="cookie-toggle">
                <input
                  type="checkbox"
                  id="preferences"
                  checked={cookiePreferences.preferences}
                  onChange={() => handleToggle('preferences')}
                  className="cookie-checkbox"
                />
                <label htmlFor="preferences" className="cookie-switch"></label>
              </div>
            </div>
            <p className="cookie-setting-description">
              Estas cookies permiten que el sitio web recuerde las opciones que has elegido (como tu idioma o
              región) y proporcionan características mejoradas y más personales. También pueden utilizarse para
              recordar los cambios realizados en el tamaño del texto, fuentes y otras partes personalizables.
            </p>
          </div>

          <div className="cookie-setting-item">
            <div className="cookie-setting-header">
              <div className="cookie-setting-info">
                <h3>Cookies de Análisis</h3>
                <span className={`cookie-badge ${cookiePreferences.analytics ? 'cookie-badge-active' : 'cookie-badge-inactive'}`}>
                  {cookiePreferences.analytics ? 'Activas' : 'Inactivas'}
                </span>
              </div>
              <div className="cookie-toggle">
                <input
                  type="checkbox"
                  id="analytics"
                  checked={cookiePreferences.analytics}
                  onChange={() => handleToggle('analytics')}
                  className="cookie-checkbox"
                />
                <label htmlFor="analytics" className="cookie-switch"></label>
              </div>
            </div>
            <p className="cookie-setting-description">
              Estas cookies nos permiten contar las visitas y fuentes de tráfico para poder medir y mejorar el
              rendimiento de nuestro sitio. Nos ayudan a saber qué páginas son las más y menos populares, y ver
              cómo se mueven los visitantes por el sitio. Toda la información que recogen estas cookies es agregada
              y, por lo tanto, anónima.
            </p>
          </div>

          <div className="cookie-setting-item">
            <div className="cookie-setting-header">
              <div className="cookie-setting-info">
                <h3>Cookies de Marketing</h3>
                <span className={`cookie-badge ${cookiePreferences.marketing ? 'cookie-badge-active' : 'cookie-badge-inactive'}`}>
                  {cookiePreferences.marketing ? 'Activas' : 'Inactivas'}
                </span>
              </div>
              <div className="cookie-toggle">
                <input
                  type="checkbox"
                  id="marketing"
                  checked={cookiePreferences.marketing}
                  onChange={() => handleToggle('marketing')}
                  className="cookie-checkbox"
                />
                <label htmlFor="marketing" className="cookie-switch"></label>
              </div>
            </div>
            <p className="cookie-setting-description">
              Estas cookies pueden ser establecidas a través de nuestro sitio por nuestros socios publicitarios.
              Pueden ser utilizadas por esas empresas para crear un perfil de tus intereses y mostrarte anuncios
              relevantes en otros sitios. No almacenan información personal directamente, sino que se basan en la
              identificación única de tu navegador y dispositivo de Internet.
            </p>
          </div>
        </div>

        <div className="cookie-actions">
          <button onClick={handleSavePreferences} className="cookie-btn cookie-btn-primary">
            Guardar Preferencias
          </button>
          <button onClick={handleAcceptAll} className="cookie-btn cookie-btn-success">
            Aceptar Todas
          </button>
          <button onClick={handleRejectAll} className="cookie-btn cookie-btn-secondary">
            Rechazar Opcionales
          </button>
        </div>

        <div className="legal-back-link">
          <Link to="/">Volver al Inicio</Link>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracionCookies;
